import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaCalendarCheck, FaHandHoldingUsd, FaHashtag, FaReceipt } from "react-icons/fa";
import axiosInstance from "../../components/api/axiosInstance";
import FinancePageHead, { FinanceStats } from "../../components/FinancePageHead";
import { ResultsMobileCard, ResultsMobileList } from "../../components/ResultsMobile";
import TablePagination, { DEFAULT_ROWS_PER_PAGE_OPTIONS, readStoredRowsPerPage } from "../../components/TablePagination";
import { mapCustomerListItemFromApi, getTenantCompanyLabel } from "../../utils/formatters";
import { ADMIN_FINANCE_PATHS, FINANCE_SECTIONS } from "../../utils/financeTerms";
import "../../assets/css/DigixLeads.css";
import "../../assets/css/AccountsReceivable.css";
import "../../assets/css/FinanceManagement.css";

const ROWS_STORAGE_KEY = "financePaymentsReceivedRowsPerPage";

const listFrom = (res) => (Array.isArray(res?.data) ? res.data : res?.data?.data || []);

const formatMoney = (v) =>
  (Number(v) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function statusTone(status) {
  const s = String(status || "").toLowerCase();
  if (s === "completed") return "success";
  if (s === "pending") return "warning";
  if (s === "failed" || s === "cancelled") return "danger";
  return "neutral";
}

const counts = (p) => String(p.Payment_Status || "Completed") === "Completed";

/** Finance > Payments Received: every payment recorded against a client invoice. */
function FinancePaymentsReceivedPage() {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [invoicesById, setInvoicesById] = useState({});
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [clientId, setClientId] = useState("");
  const [mode, setMode] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(() => readStoredRowsPerPage(ROWS_STORAGE_KEY));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [payRes, arRes, clientRes] = await Promise.all([
          axiosInstance.get("/fico/ar-payments"),
          axiosInstance.get("/fico/accounts-receivable", { params: { include_items: false } }).catch(() => ({ data: [] })),
          axiosInstance.get("/clients").catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setPayments(listFrom(payRes));
        setInvoicesById(Object.fromEntries(listFrom(arRes).map((inv) => [String(inv.AR_Id), inv])));
        setClients(
          listFrom(clientRes)
            .map((item) => mapCustomerListItemFromApi(item))
            .filter(Boolean)
            .sort((a, b) => getTenantCompanyLabel(a).localeCompare(getTenantCompanyLabel(b)))
        );
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || "Could not load payments.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const clientLabel = useMemo(() => {
    const map = Object.fromEntries(clients.map((c) => [String(c.id), getTenantCompanyLabel(c)]));
    return (id) => map[String(id)] || "";
  }, [clients]);

  const rows = useMemo(
    () =>
      payments
        .map((p) => {
          const inv = invoicesById[String(p.AR_Id)];
          return {
            ...p,
            Invoice_No: inv?.Invoice_No || `#${p.AR_Id}`,
            Client_Name: clientLabel(p.client_id) || inv?.Customer_Name || `Client #${p.client_id}`,
          };
        })
        .sort((a, b) => String(b.Payment_Date || "").localeCompare(String(a.Payment_Date || ""))),
    [payments, invoicesById, clientLabel]
  );

  const modes = useMemo(
    () => Array.from(new Set(rows.map((r) => r.Payment_Mode_Name).filter(Boolean))).sort(),
    [rows]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (clientId && String(r.client_id) !== clientId) return false;
      if (mode && r.Payment_Mode_Name !== mode) return false;
      const d = String(r.Payment_Date || "").slice(0, 10);
      if (from && d < from) return false;
      if (to && d > to) return false;
      if (!q) return true;
      return [r.Invoice_No, r.Client_Name, r.Transaction_Ref, r.Payment_Mode_Name]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [rows, clientId, mode, from, to, search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [clientId, mode, from, to, search]);

  const stats = useMemo(() => {
    const now = new Date();
    const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const year = String(now.getFullYear());
    const done = rows.filter(counts);
    return {
      thisMonth: done.filter((r) => String(r.Payment_Date || "").startsWith(month)).reduce((s, r) => s + Number(r.Amount || 0), 0),
      thisYear: done.filter((r) => String(r.Payment_Date || "").startsWith(year)).reduce((s, r) => s + Number(r.Amount || 0), 0),
      shown: filtered.filter(counts).reduce((s, r) => s + Number(r.Amount || 0), 0),
    };
  }, [rows, filtered]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const page = Math.min(currentPage, totalPages);
  const pageRows = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const hasFilters = Boolean(clientId || mode || from || to || search.trim());
  const emptyLabel = hasFilters ? "No payments match these filters." : "No payments recorded yet.";

  const openInvoices = (cid) =>
    navigate(`${ADMIN_FINANCE_PATHS.FinanceInvoices}?client=${cid}`, { state: { filterClientId: cid } });

  const clearFilters = () => {
    setClientId("");
    setMode("");
    setFrom("");
    setTo("");
    setSearch("");
  };

  const handleRowsPerPageChange = (next) => {
    setRowsPerPage(next);
    setCurrentPage(1);
    try {
      localStorage.setItem(ROWS_STORAGE_KEY, String(next));
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="digixleads-container finance-management-page">
      <FinancePageHead section={FINANCE_SECTIONS.paymentsReceived} />
      <div className="main-card">
        <div className="finance-management-tab-body accounts-payable">
          <FinanceStats
            items={[
              { key: "month", label: "Received this month", value: loading ? "—" : formatMoney(stats.thisMonth), icon: FaCalendarCheck, tone: "success" },
              { key: "year", label: "Received this year", value: loading ? "—" : formatMoney(stats.thisYear), icon: FaHandHoldingUsd, tone: "info" },
              {
                key: "shown",
                label: hasFilters ? "Matching your filters" : "All time",
                value: loading ? "—" : formatMoney(stats.shown),
                icon: FaHashtag,
                tone: "neutral",
                hint: `${filtered.length} payment${filtered.length === 1 ? "" : "s"}`,
              },
            ]}
          />

          <div className="finance-filter-bar">
            <input
              type="text"
              className="search-filter-input"
              placeholder="Search invoice, client, reference..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search payments"
            />
            <select
              className="search-filter-input search-filter-select"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              aria-label="Client"
            >
              <option value="">All clients</option>
              {clients.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {getTenantCompanyLabel(c)}
                </option>
              ))}
            </select>
            <select
              className="search-filter-input search-filter-select"
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              aria-label="Payment mode"
            >
              <option value="">All modes</option>
              {modes.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
            <label className="finance-filter-date">
              From
              <input type="date" className="search-filter-input" value={from} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label className="finance-filter-date">
              To
              <input type="date" className="search-filter-input" value={to} onChange={(e) => setTo(e.target.value)} />
            </label>
            <button type="button" className="btn-clear" onClick={clearFilters}>
              Clear
            </button>
          </div>

          {error ? <div className="accounts-payable-alert">{error}</div> : null}

          <div className="accounts-payable-table-wrap co-desktop-table">
            <table className="accounts-payable-table finance-tenants-table">
              <thead>
                <tr>
                  <th className="accounts-payable-sticky-first">Paid on</th>
                  <th>Client</th>
                  <th>Invoice</th>
                  <th className="finance-tenants-num">Amount</th>
                  <th>Mode</th>
                  <th>Reference</th>
                  <th>Status</th>
                  <th>Recorded by</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="accounts-payable-empty">
                      Loading payments...
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="accounts-payable-empty">
                      {emptyLabel}
                    </td>
                  </tr>
                ) : (
                  pageRows.map((p) => (
                    <tr key={p.Payment_Id}>
                      <td className="accounts-payable-sticky-first">{formatDate(p.Payment_Date)}</td>
                      <td>{p.Client_Name}</td>
                      <td>
                        <span
                          className="table-name-link"
                          role="button"
                          tabIndex={0}
                          onClick={() => openInvoices(p.client_id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              openInvoices(p.client_id);
                            }
                          }}
                        >
                          {p.Invoice_No}
                        </span>
                      </td>
                      <td className="finance-tenants-num">
                        {p.Currency_Code ? `${p.Currency_Code} ` : ""}
                        {formatMoney(p.Amount)}
                      </td>
                      <td>{p.Payment_Mode_Name || "—"}</td>
                      <td>{p.Transaction_Ref || "—"}</td>
                      <td>
                        <span className={`finance-tenants-status finance-tenants-status--${statusTone(p.Payment_Status)}`}>
                          {p.Payment_Status || "Completed"}
                        </span>
                      </td>
                      <td>{p.Created_By_Name || "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <ResultsMobileList loading={loading} loadingLabel="Loading payments..." emptyLabel={emptyLabel} className="finance-tenants-mobile">
            {!loading &&
              pageRows.map((p) => (
                <ResultsMobileCard
                  key={p.Payment_Id}
                  title={`${p.Currency_Code ? `${p.Currency_Code} ` : ""}${formatMoney(p.Amount)}`}
                  titleIcon={<FaReceipt />}
                  subtitle={p.Client_Name}
                  meta={[{ label: p.Payment_Status || "Completed", tone: statusTone(p.Payment_Status) }]}
                  fields={[
                    { label: "Paid on", value: formatDate(p.Payment_Date) },
                    { label: "Invoice", value: p.Invoice_No },
                    { label: "Mode", value: p.Payment_Mode_Name || "—" },
                    { label: "Reference", value: p.Transaction_Ref || "—" },
                  ]}
                  onOpen={() => openInvoices(p.client_id)}
                />
              ))}
          </ResultsMobileList>

          {!loading && filtered.length > 0 ? (
            <TablePagination
              currentPage={page}
              totalItems={filtered.length}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
              onPageChange={setCurrentPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              itemLabel="payments"
              rowsPerPageSelectId="finance-payments-rows-per-page"
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default FinancePaymentsReceivedPage;
